'use client'

/**
 * batch-67 middelen-inname (FO-499 §A4) - MiddelFormulier: het ene gedeelde
 * uitgifte-/inname-/overdrachts-/wisselformulier voor bedrijfsmiddelen.
 *
 * Eén component voor de onboarding-uitgifte, de offboarding-inname, een
 * tussentijdse wissel en gewone uitgifte of inname; portal-first (telefoon in
 * het veld) en bruikbaar op de tablet aan de balie:
 *  - één kolom, knoppen en keuzes minimaal 44 px;
 *  - per middel de staat, bewijsfoto's (meerdere, camera direct), een opmerking
 *    en waar nodig de locatie, het aantal, de retourdatum of afboeken met reden;
 *  - de handtekening van afgever en ontvanger op het bestaande canvas;
 *  - een vaste actiebalk onderaan die de safe-area respecteert.
 *
 * Presentational: de app levert de middelen aan, doet de foto-upload
 * (`onFotoUpload`) en het versturen (`onSubmit`) zelf (server-action, of de
 * offline-wachtrij in de portal) en geeft server- en schemafouten terug als
 * veldfouten; die verschijnen inline op het veld. De validatieregels zelf staan
 * in `lib/middel-formulier.ts` (puur, los getest) en spiegelen het gedeelde
 * request-schema.
 *
 * Concept-buffer: met een `conceptSleutel` overleeft de tekstuele invoer een
 * herlaad of een weggevallen verbinding (localStorage). Foto's en
 * handtekeningen horen daar bewust niet bij.
 *
 * Geen i18n-dependency in dit pakket (net als LeverwijzeKiezer): NL-teksten met
 * een override via `labels`. STREEP-tokens inline.
 */

import * as React from 'react'
import { ArrowDownToLine, ArrowUpFromLine, Loader2, Trash2 } from 'lucide-react'
import { cn } from '../lib/utils'
import { Combobox } from './combobox'
import { DatePicker } from './date-picker'
import { Input } from './input'
import { Textarea } from './textarea'
import { SignaturePad, type SignaturePadHandle } from './signature-pad'
import { MultiPhotoUpload, type MultiPhotoUploadUitkomst } from './multi-photo-upload'
import {
  MIDDEL_FORMULIER_AFBOEK_SOORTEN,
  MIDDEL_FORMULIER_STATEN,
  fotoVerplichtVoorRegel,
  regelOndertitel,
  legeRegelWaarde,
  locatieVereistVoorRegel,
  middelFormulierRichting,
  conceptItems,
  conceptVerlopen,
  naarConcept,
  pasConceptToe,
  regelFoutPad,
  valideerMiddelFormulier,
  verplichteHandtekeningen,
  type MiddelFormulierConceptItem,
  type MiddelFormulierItem,
  type MiddelFormulierModus,
  type MiddelFormulierRegelWaarde,
  type MiddelFormulierWaarden,
} from '../lib/middel-formulier'

export type MiddelFormulierSubmitUitkomst =
  | { ok: true; inWachtrij?: boolean }
  | {
      ok: false
      /** Algemene melding (bovenaan de actiebalk). */
      fout?: string
      /** Veldfouten per pad: `handtekeningOntvanger`, `regels.<key>.fotoIds`, of `regels.<key>` voor de hele regel. */
      veldFouten?: Record<string, string>
    }

export interface MiddelFormulierLabels {
  titelMiddelen: string
  geenMiddelen: string
  staat: string
  opmerkingRegel: string
  opmerkingSessie: string
  plek: string
  plekPlaceholder: string
  afboeken: string
  afboekSoort: string
  afboekReden: string
  locatieTerug: string
  locatieVoorraad: string
  locatiePlaceholder: string
  retourdatum: string
  aantal: string
  komtTerug: string
  gaatMee: string
  fotoKnop: string
  verwijderen: string
  bezig: string
  wachtOpFotos: string
  controleer: string
  handtekeningWissen: string
  versturen: Record<MiddelFormulierModus, string>
}

const STANDAARD_LABELS: MiddelFormulierLabels = {
  titelMiddelen: 'Middelen',
  geenMiddelen: 'Er staan nog geen middelen op dit formulier.',
  staat: 'Staat',
  opmerkingRegel: 'Opmerking bij dit middel (optioneel)',
  opmerkingSessie: 'Opmerking (optioneel)',
  plek: 'Plek',
  plekPlaceholder: 'Bijvoorbeeld: balie kantoor',
  afboeken: 'Kapot of kwijt: afboeken in plaats van terug op voorraad',
  afboekSoort: 'Kapot of kwijt',
  afboekReden: 'Reden van afboeken (verplicht)',
  locatieTerug: 'Terugleggen op',
  locatieVoorraad: 'Voorraadlocatie',
  locatiePlaceholder: 'Kies een locatie...',
  retourdatum: 'Verwachte retourdatum',
  aantal: 'Aantal',
  komtTerug: 'Komt terug',
  gaatMee: 'Gaat mee',
  fotoKnop: 'Foto toevoegen',
  verwijderen: 'Van het formulier halen',
  bezig: 'Bezig...',
  wachtOpFotos: "Even wachten: de foto's worden nog verstuurd.",
  controleer: 'Controleer de gemarkeerde velden.',
  handtekeningWissen: 'Opnieuw tekenen',
  versturen: {
    UITGIFTE: 'Uitgifte vastleggen',
    INNAME: 'Inname vastleggen',
    OVERDRACHT: 'Overdracht vastleggen',
    WISSEL: 'Wissel vastleggen',
  },
}

export interface MiddelFormulierProps {
  modus: MiddelFormulierModus
  /** De medewerker om wie het gaat (ontvanger bij uitgifte, afgever bij inname en overdracht). */
  medewerkerNaam: string
  /** Bij een overdracht: de collega die het middel overneemt. */
  tegenpartijNaam?: string | null
  /** De ingelogde gebruiker die het formulier invult (de STREEP-kant van de handtekening). */
  gebruikerNaam?: string | null
  items: ReadonlyArray<MiddelFormulierItem>
  locaties?: ReadonlyArray<{ id: string; naam: string }>
  /** Upload één bewijsfoto; geeft de foto-id terug (of een plek in de offline-wachtrij). */
  onFotoUpload: (file: File, itemKey: string) => Promise<MultiPhotoUploadUitkomst>
  onSubmit: (waarden: MiddelFormulierWaarden) => Promise<MiddelFormulierSubmitUitkomst>
  /** Na een geslaagde verzending (de app navigeert of toont zijn bevestiging). */
  onKlaar?: (uitkomst: { inWachtrij: boolean }) => void
  /** Haal een middel van het formulier (de app beheert de lijst). */
  onItemVerwijder?: (key: string) => void
  /**
   * Toon de verwijder-actie als tekstknop met dit label in plaats van het
   * prullenbak-icoon (bv. "Niet ingeleverd" op het innamescherm van de offboarding).
   */
  verwijderTekst?: string
  /** Slot boven de lijst, bv. de picker om middelen toe te voegen. */
  toevoegSlot?: React.ReactNode
  /** Slot direct onder de lijst en vóór de handtekeningen (bv. wat NIET wordt ingeleverd). */
  naMiddelenSlot?: React.ReactNode
  /**
   * Extra velden van de app binnen de kaart van één middel, direct onder de staat
   * (bv. "terug op voorraad of afvoeren" bij de offboarding-inname).
   * De app houdt die waarden zelf bij en geeft fouten terug als veldfout
   * `regels.<key>.<veld>`; `fout(veld)` leest die uit.
   */
  renderRegelExtra?: (ctx: MiddelFormulierRegelExtraContext) => React.ReactNode
  /** Sleutel van de concept-buffer (localStorage); zonder sleutel geen buffer. */
  conceptSleutel?: string
  /**
   * Na een herlaad: de middelen die in het bewaarde concept op het formulier
   * stonden en er nu niet (meer) op staan. De app beslist welke terug mogen en
   * zet ze in `items`; hun bewaarde invoer komt dan vanzelf terug.
   */
  onConceptItems?: (items: MiddelFormulierConceptItem[]) => void
  /**
   * Klassen van de vaste actiebalk. Default onderaan het scherm met safe-area;
   * de portal geeft hier de clearance boven de BottomNav mee.
   */
  actiebalkClassName?: string
  labels?: Partial<MiddelFormulierLabels>
  className?: string
  disabled?: boolean
}

export interface MiddelFormulierRegelExtraContext {
  item: MiddelFormulierItem
  regel: MiddelFormulierRegelWaarde
  /** De veldfout van `regels.<key>.<veld>`, of null. */
  fout: (veld: string) => string | null
  /** Wis de veldfouten van deze velden (na een wijziging door de gebruiker). */
  wisFout: (...velden: string[]) => void
  disabled: boolean
}

/** Eén handtekeningvak met een knop om opnieuw te tekenen. */
function HandtekeningVak({
  label,
  verplicht,
  padRef,
  fout,
  onWijzig,
  wisLabel,
  disabled,
}: {
  label: string
  verplicht: boolean
  padRef: React.RefObject<SignaturePadHandle>
  fout: string | null
  onWijzig: () => void
  wisLabel: string
  disabled: boolean
}) {
  return (
    <div className={cn('rounded-lg border bg-white p-4', fout ? 'border-red-400' : 'border-[#CED0C8]')}>
      <div className="mb-2 flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-[#1A1A18]">
          {label}
          {verplicht ? <span className="text-red-700"> *</span> : null}
        </p>
        <button
          type="button"
          onClick={() => padRef.current?.clear()}
          disabled={disabled}
          className="min-h-11 shrink-0 rounded-md px-3 text-sm text-[#5C5C58] underline-offset-2 hover:underline disabled:opacity-50 md:min-h-0 md:py-1"
        >
          {wisLabel}
        </button>
      </div>
      <SignaturePad ref={padRef} onChange={onWijzig} />
      {fout ? <p className="mt-1 text-sm text-red-700">{fout}</p> : null}
    </div>
  )
}

function leesConcept(sleutel: string | undefined): unknown {
  if (!sleutel || typeof window === 'undefined') return null
  try {
    const ruw = window.localStorage.getItem(sleutel)
    if (!ruw) return null
    const concept = JSON.parse(ruw) as unknown
    // Een verlopen concept (ouder dan een week) blijft niet in de browser staan.
    if (conceptVerlopen(concept)) {
      window.localStorage.removeItem(sleutel)
      return null
    }
    return concept
  } catch {
    // Privévenster, geblokkeerde opslag of verminkte inhoud: het formulier werkt ook zonder.
    return null
  }
}

function schrijfConcept(
  sleutel: string | undefined,
  waarden: MiddelFormulierWaarden | null,
  items: ReadonlyArray<MiddelFormulierItem> = [],
): void {
  if (!sleutel || typeof window === 'undefined') return
  try {
    if (waarden) window.localStorage.setItem(sleutel, JSON.stringify(naarConcept(waarden, items)))
    else window.localStorage.removeItem(sleutel)
  } catch {
    // Best-effort.
  }
}

export function MiddelFormulier({
  modus,
  medewerkerNaam,
  tegenpartijNaam = null,
  gebruikerNaam = null,
  items,
  locaties = [],
  onFotoUpload,
  onSubmit,
  onKlaar,
  onItemVerwijder,
  verwijderTekst,
  toevoegSlot,
  naMiddelenSlot,
  renderRegelExtra,
  conceptSleutel,
  onConceptItems,
  actiebalkClassName,
  labels,
  className,
  disabled = false,
}: MiddelFormulierProps) {
  const t = React.useMemo(() => ({ ...STANDAARD_LABELS, ...labels }), [labels])
  const [waarden, setWaarden] = React.useState<MiddelFormulierWaarden>(() => ({
    plek: '',
    opmerking: '',
    handtekeningAfgever: null,
    handtekeningOntvanger: null,
    regels: [],
  }))
  const [fouten, setFouten] = React.useState<Record<string, string>>({})
  const [algemeneFout, setAlgemeneFout] = React.useState<string | null>(null)
  const [bezig, setBezig] = React.useState(false)
  const [fotosBezig, setFotosBezig] = React.useState<Record<string, boolean>>({})
  const afgeverRef = React.useRef<SignaturePadHandle>(null)
  const ontvangerRef = React.useRef<SignaturePadHandle>(null)
  // Het bij het openen gelezen concept (undefined = nog niet gelezen) en de
  // regels die hun bewaarde invoer al terugkregen.
  const conceptRef = React.useRef<unknown>(undefined)
  const conceptToegepast = React.useRef(new Set<string>())
  const onConceptItemsRef = React.useRef(onConceptItems)
  onConceptItemsRef.current = onConceptItems
  // Pas opslaan nadat de regels (en het bewaarde concept) in de toestand staan:
  // anders overschrijft de lege starttoestand het concept dat nog gelezen moet worden.
  const [gesynct, setGesynct] = React.useState(false)

  // Houd de regels gelijk met de aangeleverde middelen: nieuwe middelen krijgen
  // hun startwaarde, verdwenen middelen vallen weg, bestaande invoer blijft. Een
  // regel die voor het eerst op het formulier komt, krijgt zijn bewaarde invoer
  // terug (ook als de app hem pas na het openen terugzet). Het concept wordt HIER
  // gelezen en niet in de updater: die moet zuiver blijven (React kan hem twee
  // keer aanroepen).
  React.useEffect(() => {
    const eersteKeer = conceptRef.current === undefined
    if (eersteKeer) {
      conceptRef.current = leesConcept(conceptSleutel)
      const opFormulier = new Set(items.map((i) => i.key))
      const bewaard = conceptItems(conceptRef.current).filter((i) => !opFormulier.has(i.key))
      if (bewaard.length > 0) onConceptItemsRef.current?.(bewaard)
    }
    const concept = conceptRef.current
    const nieuw = new Set(items.map((i) => i.key).filter((k) => !conceptToegepast.current.has(k)))
    for (const k of nieuw) conceptToegepast.current.add(k)
    setWaarden((huidig) => {
      const bestaand = new Map(huidig.regels.map((r) => [r.key, r]))
      const volgende: MiddelFormulierWaarden = {
        ...huidig,
        regels: items.map((item) => bestaand.get(item.key) ?? legeRegelWaarde(modus, item)),
      }
      return eersteKeer || nieuw.size > 0 ? pasConceptToe(volgende, concept, { keys: nieuw, sessievelden: eersteKeer }) : volgende
    })
    setGesynct(true)
  }, [items, modus, conceptSleutel])

  // Bewaar het concept bij elke wijziging (tekstuele invoer en welke middelen erop staan).
  React.useEffect(() => {
    if (!gesynct) return
    schrijfConcept(conceptSleutel, waarden, items)
  }, [waarden, items, conceptSleutel, gesynct])

  const itemByKey = React.useMemo(() => new Map(items.map((i) => [i.key, i])), [items])
  const locatieOpties = React.useMemo(() => locaties.map((l) => ({ value: l.id, label: l.naam })), [locaties])
  const verplicht = verplichteHandtekeningen(modus)
  const fotoUploadBezig = Object.values(fotosBezig).some(Boolean)

  function wisFout(...paden: string[]) {
    setFouten((f) => {
      if (!paden.some((p) => p in f)) return f
      const kopie = { ...f }
      for (const p of paden) delete kopie[p]
      return kopie
    })
  }

  function zetRegel(key: string, patch: Partial<MiddelFormulierRegelWaarde>) {
    setWaarden((w) => ({ ...w, regels: w.regels.map((r) => (r.key === key ? { ...r, ...patch } : r)) }))
    wisFout(...Object.keys(patch).map((veld) => regelFoutPad(key, veld)), `regels.${key}`)
  }

  const naamAfgever =
    modus === 'UITGIFTE' || modus === 'WISSEL' ? (gebruikerNaam ?? 'STREEP') : medewerkerNaam
  const naamOntvanger =
    modus === 'UITGIFTE' || modus === 'WISSEL'
      ? medewerkerNaam
      : modus === 'OVERDRACHT'
        ? (tegenpartijNaam ?? 'de collega')
        : (gebruikerNaam ?? 'STREEP')
  const labelAfgever =
    modus === 'INNAME'
      ? `Handtekening van wie inlevert (${naamAfgever})`
      : modus === 'OVERDRACHT'
        ? `Handtekening van wie afgeeft (${naamAfgever})`
        : `Handtekening uitgever (${naamAfgever})`
  const labelOntvanger =
    modus === 'INNAME' ? `Handtekening voor ontvangst (${naamOntvanger})` : `Handtekening ontvanger (${naamOntvanger})`

  async function verstuur() {
    if (bezig || disabled) return
    setAlgemeneFout(null)
    if (fotoUploadBezig) {
      setAlgemeneFout(t.wachtOpFotos)
      return
    }
    const compleet: MiddelFormulierWaarden = {
      ...waarden,
      handtekeningAfgever: afgeverRef.current && !afgeverRef.current.isEmpty() ? afgeverRef.current.toDataURL() : null,
      handtekeningOntvanger: ontvangerRef.current && !ontvangerRef.current.isEmpty() ? ontvangerRef.current.toDataURL() : null,
    }
    const lokaal = valideerMiddelFormulier(modus, compleet, items)
    if (Object.keys(lokaal).length > 0) {
      setFouten(lokaal)
      setAlgemeneFout(lokaal['regels'] ?? t.controleer)
      return
    }
    setBezig(true)
    try {
      const uitkomst = await onSubmit(compleet)
      if (uitkomst.ok) {
        schrijfConcept(conceptSleutel, null)
        setFouten({})
        onKlaar?.({ inWachtrij: Boolean(uitkomst.inWachtrij) })
        return
      }
      setFouten(uitkomst.veldFouten ?? {})
      setAlgemeneFout(uitkomst.fout ?? (uitkomst.veldFouten ? t.controleer : 'Vastleggen is niet gelukt. Probeer het opnieuw.'))
    } catch {
      setAlgemeneFout('Vastleggen is niet gelukt. Probeer het opnieuw.')
    } finally {
      setBezig(false)
    }
  }

  const geblokkeerd = disabled || bezig

  return (
    <div className={cn('space-y-4', className)}>
      {toevoegSlot}

      <section className="space-y-3" aria-label={t.titelMiddelen}>
        {waarden.regels.length === 0 ? (
          <p className="rounded-lg border border-[#CED0C8] bg-white p-4 text-sm text-[#5C5C58]">{t.geenMiddelen}</p>
        ) : null}
        {waarden.regels.map((regel) => {
          const item = itemByKey.get(regel.key)
          if (!item) return null
          const richting = middelFormulierRichting(modus, regel)
          const fout = (veld: string) => fouten[regelFoutPad(regel.key, veld)] ?? null
          const regelFout = fouten[`regels.${regel.key}`] ?? null
          const toonAfboeken = richting === 'IN' && item.kanAfboeken !== false
          // Bij afboeken volgt de staat uit de keuze kapot of kwijt.
          const toonStaat = richting !== 'UIT' && !regel.afboeken && !item.zonderStaat
          const toonLocatie = locatieVereistVoorRegel(item, regel) && richting !== 'OVER' && !regel.afboeken
          const toonRetourdatum = Boolean(item.vereistRetourdatum) && richting === 'UIT'
          const fotoVerplicht = fotoVerplichtVoorRegel(regel)
          return (
            <article
              key={regel.key}
              className={cn(
                'rounded-lg border bg-white p-4',
                regelFout || Object.keys(fouten).some((k) => k.startsWith(`regels.${regel.key}.`)) ? 'border-red-400' : 'border-[#CED0C8]',
              )}
            >
              <header className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-[#1A1A18]">
                    {regel.aantal > 1 ? `${regel.aantal}x ` : ''}
                    {item.omschrijving}
                  </h3>
                  <p className="text-xs text-[#5C5C58]">
                    {regelOndertitel(item) || ' '}
                  </p>
                </div>
                {onItemVerwijder && verwijderTekst ? (
                  <button
                    type="button"
                    onClick={() => onItemVerwijder(regel.key)}
                    disabled={geblokkeerd}
                    className="min-h-11 shrink-0 rounded-md border border-[#CED0C8] bg-white px-3 text-sm text-[#1A1A18] hover:bg-[#F0F1EC] disabled:opacity-50"
                  >
                    {verwijderTekst}
                  </button>
                ) : onItemVerwijder ? (
                  <button
                    type="button"
                    onClick={() => onItemVerwijder(regel.key)}
                    disabled={geblokkeerd}
                    aria-label={`${t.verwijderen}: ${item.omschrijving}`}
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-[#5C5C58] hover:bg-[#F0F1EC] disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                ) : null}
              </header>

              <div className="mt-3 space-y-3">
                {modus === 'WISSEL' && item.richting === undefined ? (
                  <div>
                    <div className="flex flex-wrap gap-2">
                      {(['IN', 'UIT'] as const).map((r) => (
                        <button
                          key={r}
                          type="button"
                          disabled={geblokkeerd}
                          onClick={() => zetRegel(regel.key, { richting: r, conditie: r === 'UIT' ? 'GOED' : null, afboeken: false })}
                          className={cn(
                            'inline-flex min-h-11 items-center gap-2 rounded-md border px-4 text-sm',
                            regel.richting === r ? 'border-[#1A1A18] bg-[#CCFA78] font-semibold' : 'border-[#CED0C8] bg-white',
                          )}
                        >
                          {r === 'IN' ? <ArrowDownToLine className="h-4 w-4" aria-hidden="true" /> : <ArrowUpFromLine className="h-4 w-4" aria-hidden="true" />}
                          {r === 'IN' ? t.komtTerug : t.gaatMee}
                        </button>
                      ))}
                    </div>
                    {fout('richting') ? <p className="mt-1 text-sm text-red-700">{fout('richting')}</p> : null}
                  </div>
                ) : null}

                {item.aantalBewerkbaar ? (
                  <div>
                    <label className="mb-1 block text-sm font-medium text-[#1A1A18]" htmlFor={`aantal-${regel.key}`}>
                      {t.aantal}
                    </label>
                    <Input
                      id={`aantal-${regel.key}`}
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={item.maxAantal}
                      value={String(regel.aantal)}
                      disabled={geblokkeerd}
                      onChange={(e) => zetRegel(regel.key, { aantal: Math.max(1, Math.trunc(Number(e.target.value) || 1)) })}
                      className="w-28"
                    />
                    {fout('aantal') ? <p className="mt-1 text-sm text-red-700">{fout('aantal')}</p> : null}
                  </div>
                ) : null}

                {toonStaat ? (
                  <div>
                    <p className="mb-1 text-sm font-medium text-[#1A1A18]">{t.staat}</p>
                    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t.staat}>
                      {MIDDEL_FORMULIER_STATEN.map((st) => (
                        <button
                          key={st.value}
                          type="button"
                          role="radio"
                          aria-checked={regel.conditie === st.value}
                          disabled={geblokkeerd}
                          onClick={() => zetRegel(regel.key, { conditie: st.value })}
                          className={cn(
                            'min-h-11 rounded-md border px-4 text-sm',
                            regel.conditie === st.value ? 'border-[#1A1A18] bg-[#CCFA78] font-semibold' : 'border-[#CED0C8] bg-white',
                          )}
                        >
                          {st.label}
                        </button>
                      ))}
                    </div>
                    {fout('conditie') ? <p className="mt-1 text-sm text-red-700">{fout('conditie')}</p> : null}
                  </div>
                ) : null}

                {renderRegelExtra
                  ? renderRegelExtra({
                      item,
                      regel,
                      fout,
                      wisFout: (...velden) => wisFout(...velden.map((veld) => regelFoutPad(regel.key, veld)), `regels.${regel.key}`),
                      disabled: geblokkeerd,
                    })
                  : null}

                {toonAfboeken ? (
                  <div>
                    <label className="flex min-h-11 items-center gap-3 text-sm text-[#1A1A18]">
                      <input
                        type="checkbox"
                        className="h-5 w-5"
                        checked={regel.afboeken}
                        disabled={geblokkeerd}
                        onChange={(e) => zetRegel(regel.key, { afboeken: e.target.checked, afboekSoort: null })}
                      />
                      {t.afboeken}
                    </label>
                    {regel.afboeken ? (
                      <div className="mt-2 space-y-2">
                        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t.afboekSoort}>
                          {MIDDEL_FORMULIER_AFBOEK_SOORTEN.map((soort) => (
                            <button
                              key={soort.value}
                              type="button"
                              role="radio"
                              aria-checked={regel.afboekSoort === soort.value}
                              disabled={geblokkeerd}
                              onClick={() => zetRegel(regel.key, { afboekSoort: soort.value })}
                              className={cn(
                                'min-h-11 rounded-md border px-4 text-sm',
                                regel.afboekSoort === soort.value ? 'border-[#1A1A18] bg-[#CCFA78] font-semibold' : 'border-[#CED0C8] bg-white',
                              )}
                            >
                              {soort.label}
                            </button>
                          ))}
                        </div>
                        {fout('afboekSoort') ? <p className="text-sm text-red-700">{fout('afboekSoort')}</p> : null}
                        <Textarea
                          value={regel.afboekReden}
                          disabled={geblokkeerd}
                          onChange={(e) => zetRegel(regel.key, { afboekReden: e.target.value })}
                          placeholder={t.afboekReden}
                          rows={2}
                          aria-label={t.afboekReden}
                        />
                        {fout('afboekReden') ? <p className="mt-1 text-sm text-red-700">{fout('afboekReden')}</p> : null}
                      </div>
                    ) : null}
                    {fout('afboeken') ? <p className="mt-1 text-sm text-red-700">{fout('afboeken')}</p> : null}
                  </div>
                ) : null}

                {toonLocatie ? (
                  <div>
                    <p className="mb-1 text-sm font-medium text-[#1A1A18]">{richting === 'IN' ? t.locatieTerug : t.locatieVoorraad}</p>
                    <Combobox
                      options={locatieOpties}
                      value={regel.locatieId}
                      onChange={(v) => zetRegel(regel.key, { locatieId: v })}
                      placeholder={t.locatiePlaceholder}
                      disabled={geblokkeerd}
                      className="min-h-11 w-full"
                    />
                    {fout('locatieId') ? <p className="mt-1 text-sm text-red-700">{fout('locatieId')}</p> : null}
                  </div>
                ) : null}

                {toonRetourdatum ? (
                  <div>
                    <p className="mb-1 text-sm font-medium text-[#1A1A18]">{t.retourdatum}</p>
                    <DatePicker
                      value={regel.verwachteRetour ?? undefined}
                      onChange={(v) => zetRegel(regel.key, { verwachteRetour: v ?? null })}
                      // Een retourdatum kan jaren vooruit liggen (einde contract):
                      // jaar- en maandkiezer plus zelf typen.
                      fromYear={new Date().getFullYear()}
                      toYear={new Date().getFullYear() + 15}
                      allowManualInput
                      disabled={geblokkeerd}
                      aria-label={t.retourdatum}
                    />
                    {fout('verwachteRetour') ? <p className="mt-1 text-sm text-red-700">{fout('verwachteRetour')}</p> : null}
                  </div>
                ) : null}

                {item.zonderStaat ? null : (
                  <MultiPhotoUpload
                    onUpload={(file) => onFotoUpload(file, regel.key)}
                    onChange={(ids) => zetRegel(regel.key, { fotoIds: ids })}
                    onBezigChange={(b) => setFotosBezig((s) => (s[regel.key] === b ? s : { ...s, [regel.key]: b }))}
                    verplicht={fotoVerplicht}
                    fout={fout('fotoIds')}
                    label={t.fotoKnop}
                    disabled={geblokkeerd}
                  />
                )}

                <Textarea
                  value={regel.opmerking}
                  disabled={geblokkeerd}
                  onChange={(e) => zetRegel(regel.key, { opmerking: e.target.value })}
                  placeholder={t.opmerkingRegel}
                  rows={2}
                  aria-label={t.opmerkingRegel}
                />

                {regelFout ? (
                  <p role="alert" className="text-sm text-red-700">
                    {regelFout}
                  </p>
                ) : null}
              </div>
            </article>
          )
        })}
      </section>

      {naMiddelenSlot}

      <section className="space-y-3 rounded-lg border border-[#CED0C8] bg-white p-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-[#1A1A18]" htmlFor="middel-formulier-plek">
            {t.plek}
          </label>
          <Input
            id="middel-formulier-plek"
            value={waarden.plek}
            disabled={geblokkeerd}
            onChange={(e) => setWaarden((w) => ({ ...w, plek: e.target.value }))}
            placeholder={t.plekPlaceholder}
          />
        </div>
        <Textarea
          value={waarden.opmerking}
          disabled={geblokkeerd}
          onChange={(e) => setWaarden((w) => ({ ...w, opmerking: e.target.value }))}
          placeholder={t.opmerkingSessie}
          rows={2}
          aria-label={t.opmerkingSessie}
        />
      </section>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <HandtekeningVak
          label={labelAfgever}
          verplicht={verplicht.afgever}
          padRef={afgeverRef}
          fout={fouten['handtekeningAfgever'] ?? null}
          onWijzig={() => wisFout('handtekeningAfgever')}
          wisLabel={t.handtekeningWissen}
          disabled={geblokkeerd}
        />
        <HandtekeningVak
          label={labelOntvanger}
          verplicht={verplicht.ontvanger}
          padRef={ontvangerRef}
          fout={fouten['handtekeningOntvanger'] ?? null}
          onWijzig={() => wisFout('handtekeningOntvanger')}
          wisLabel={t.handtekeningWissen}
          disabled={geblokkeerd}
        />
      </section>

      {/* Ruimte zodat de vaste actiebalk de laatste kaart niet afdekt. */}
      <div className="h-24" aria-hidden="true" />

      <div
        className={cn(
          'fixed inset-x-0 bottom-0 z-40 border-t border-[#CED0C8] bg-white p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]',
          actiebalkClassName,
        )}
      >
        <div className="mx-auto flex max-w-3xl flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className={cn('text-sm', algemeneFout ? 'text-red-700' : 'text-[#5C5C58]')} role={algemeneFout ? 'alert' : undefined}>
            {algemeneFout ?? `${waarden.regels.length} ${waarden.regels.length === 1 ? 'middel' : 'middelen'}`}
          </p>
          <button
            type="button"
            onClick={() => void verstuur()}
            disabled={geblokkeerd || waarden.regels.length === 0}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-[#CCFA78] px-5 text-sm font-semibold text-[#1A1A18] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {bezig ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            {bezig ? t.bezig : t.versturen[modus]}
          </button>
        </div>
      </div>
    </div>
  )
}
